/**
 * Theme Definitions
 *
 * Each theme defines a complete set of CSS variable values.
 * Themes can be applied by setting CSS variables on the document root.
 */

export type ThemeName = 'dark' | 'light' | 'high-contrast';

export interface Theme {
  name: ThemeName;
  displayName: string;
  colors: {
    // Background colors
    'nb-bg-primary': string;
    'nb-bg-secondary': string;
    'nb-bg-tertiary': string;
    'nb-bg-hover': string;
    'nb-bg-active': string;

    // Border colors
    'nb-border-primary': string;
    'nb-border-secondary': string;

    // Text colors
    'nb-text-primary': string;
    'nb-text-secondary': string;
    'nb-text-muted': string;
    'nb-text-accent': string;

    // Accent colors
    'nb-accent-primary': string;
    'nb-accent-hover': string;
    'nb-accent-secondary': string;
    'nb-accent-warning': string;
    'nb-accent-error': string;

    // Node colors
    'nb-node-bg': string;
    'nb-node-border': string;
    'nb-node-selected': string;
    'nb-node-handle': string;

    // Timeline colors
    'nb-timeline-bg': string;
    'nb-timeline-track': string;
    'nb-timeline-beat': string;
    'nb-timeline-playhead': string;
  };
}

/**
 * Dark Theme (Default)
 * Inspired by VS Code dark theme
 */
export const darkTheme: Theme = {
  name: 'dark',
  displayName: 'Dark',
  colors: {
    'nb-bg-primary': '#1e1e1e',
    'nb-bg-secondary': '#252526',
    'nb-bg-tertiary': '#2d2d30',
    'nb-bg-hover': '#3e3e42',
    'nb-bg-active': '#094771',

    'nb-border-primary': '#3e3e42',
    'nb-border-secondary': '#4e4e52',

    'nb-text-primary': '#cccccc',
    'nb-text-secondary': '#9d9d9d',
    'nb-text-muted': '#6e6e6e',
    'nb-text-accent': '#569cd6',

    'nb-accent-primary': '#0e639c',
    'nb-accent-hover': '#1177bb',
    'nb-accent-secondary': '#3c8039',
    'nb-accent-warning': '#cca700',
    'nb-accent-error': '#f14c4c',

    'nb-node-bg': '#2d2d30',
    'nb-node-border': '#4e4e52',
    'nb-node-selected': '#0e639c',
    'nb-node-handle': '#569cd6',

    'nb-timeline-bg': '#252526',
    'nb-timeline-track': '#3e3e42',
    'nb-timeline-beat': '#3c8039',
    'nb-timeline-playhead': '#f14c4c',
  },
};

/**
 * Light Theme
 * Clean, bright theme for well-lit environments
 */
export const lightTheme: Theme = {
  name: 'light',
  displayName: 'Light',
  colors: {
    'nb-bg-primary': '#ffffff',
    'nb-bg-secondary': '#f3f3f3',
    'nb-bg-tertiary': '#e8e8e8',
    'nb-bg-hover': '#d4d4d4',
    'nb-bg-active': '#cce7ff',

    'nb-border-primary': '#d4d4d4',
    'nb-border-secondary': '#c4c4c4',

    'nb-text-primary': '#1e1e1e',
    'nb-text-secondary': '#616161',
    'nb-text-muted': '#959595',
    'nb-text-accent': '#0066cc',

    'nb-accent-primary': '#0066cc',
    'nb-accent-hover': '#0052a3',
    'nb-accent-secondary': '#16825d',
    'nb-accent-warning': '#bf8803',
    'nb-accent-error': '#d13438',

    'nb-node-bg': '#ffffff',
    'nb-node-border': '#c4c4c4',
    'nb-node-selected': '#0066cc',
    'nb-node-handle': '#0066cc',

    'nb-timeline-bg': '#f3f3f3',
    'nb-timeline-track': '#d4d4d4',
    'nb-timeline-beat': '#16825d',
    'nb-timeline-playhead': '#d13438',
  },
};

/**
 * High Contrast Theme
 * Maximum contrast for accessibility
 */
export const highContrastTheme: Theme = {
  name: 'high-contrast',
  displayName: 'High Contrast',
  colors: {
    'nb-bg-primary': '#000000',
    'nb-bg-secondary': '#0a0a0a',
    'nb-bg-tertiary': '#1a1a1a',
    'nb-bg-hover': '#2a2a2a',
    'nb-bg-active': '#003d5c',

    'nb-border-primary': '#6fc3df',
    'nb-border-secondary': '#4a9fb8',

    'nb-text-primary': '#ffffff',
    'nb-text-secondary': '#d4d4d4',
    'nb-text-muted': '#a0a0a0',
    'nb-text-accent': '#36c5f0',

    'nb-accent-primary': '#36c5f0',
    'nb-accent-hover': '#5ed0f5',
    'nb-accent-secondary': '#73c991',
    'nb-accent-warning': '#ffd60a',
    'nb-accent-error': '#ff5555',

    'nb-node-bg': '#1a1a1a',
    'nb-node-border': '#ffffff',
    'nb-node-selected': '#36c5f0',
    'nb-node-handle': '#36c5f0',

    'nb-timeline-bg': '#0a0a0a',
    'nb-timeline-track': '#2a2a2a',
    'nb-timeline-beat': '#73c991',
    'nb-timeline-playhead': '#ff5555',
  },
};

/**
 * All available themes
 */
export const themes: Record<ThemeName, Theme> = {
  dark: darkTheme,
  light: lightTheme,
  'high-contrast': highContrastTheme,
};

/**
 * Apply a theme by setting CSS variables on document root
 */
export function applyTheme(themeName: ThemeName): void {
  const theme = themes[themeName];
  if (!theme) {
    console.warn(`Theme "${themeName}" not found, using dark theme`);
    return applyTheme('dark');
  }

  const root = document.documentElement;

  // Apply all color variables
  Object.entries(theme.colors).forEach(([key, value]) => {
    root.style.setProperty(`--${key}`, value);
  });
}

/**
 * Get the current theme from CSS variables
 * (Useful for detecting system theme or initial state)
 */
export function getCurrentThemeName(): ThemeName {
  const root = document.documentElement;
  const bgPrimary = getComputedStyle(root).getPropertyValue('--nb-bg-primary').trim();

  // Try to match against known themes
  for (const [name, theme] of Object.entries(themes)) {
    if (theme.colors['nb-bg-primary'] === bgPrimary) {
      return name as ThemeName;
    }
  }

  return 'dark'; // Default
}

// =============================================================================
// NODE COLOR PALETTE
// =============================================================================

/**
 * Predefined color palette for beat nodes
 * Users can choose from these colors or use a custom color picker
 */
export interface NodeColor {
  name: string;
  value: string;
  description: string;
}

export const nodeColorPalette: NodeColor[] = [
  { name: 'Blue', value: '#2d4353', description: 'Cool blue tone' }, // RGB(45, 67, 83)
  { name: 'Green', value: '#3d5338', description: 'Fresh green tone' }, // RGB(61, 83, 56)
  { name: 'Orange', value: '#53432d', description: 'Warm orange tone' }, // RGB(83, 67, 45)
  { name: 'Grey', value: '#535353', description: 'Neutral grey tone' }, // RGB(83, 83, 83)
  { name: 'Red', value: '#533838', description: 'Bold red tone' }, // RGB(83, 56, 56)
  { name: 'Purple', value: '#433853', description: 'Rich purple tone' }, // RGB(67, 56, 83)
  { name: 'Yellow', value: '#53533d', description: 'Bright yellow tone' }, // RGB(83, 83, 61)
  { name: 'Teal', value: '#2d534a', description: 'Aqua teal tone' }, // RGB(45, 83, 74)
  { name: 'Pink', value: '#533846', description: 'Soft pink tone' }, // RGB(83, 56, 70)
  { name: 'Brown', value: '#4a4338', description: 'Earthy brown tone' }, // RGB(74, 67, 56)
];

// =============================================================================
// BEAT COLOR SCHEMES
// =============================================================================

/**
 * Beat color schemes control the colors used for:
 * - Timeline beat blocks
 * - Selected node highlights
 * - Node selection borders
 */
export type BeatColorScheme = 'blue-green' | 'orange-grey';

export interface BeatColors {
  selected: string;    // Color for selected nodes and active beat
  unselected: string;  // Color for unselected timeline beats
}

export const beatColorSchemes: Record<BeatColorScheme, BeatColors> = {
  'blue-green': {
    selected: '#0e639c',   // Blue
    unselected: '#3c8039', // Green
  },
  'orange-grey': {
    selected: '#966432',   // Orange - RGB(150, 100, 50)
    unselected: '#606060', // Grey - RGB(96, 96, 96) - Brighter for visibility
  },
};

/**
 * Apply beat color scheme to CSS variables
 */
export function applyBeatColorScheme(scheme: BeatColorScheme): void {
  const colors = beatColorSchemes[scheme];
  const root = document.documentElement;

  // Selected/accent colors
  root.style.setProperty('--nb-accent-primary', colors.selected);
  root.style.setProperty('--nb-accent-hover', colors.selected);
  root.style.setProperty('--nb-node-selected', colors.selected);

  // Active background (script highlight) - use selected color with transparency
  const selectedRgb = hexToRgb(colors.selected);
  root.style.setProperty('--nb-bg-active', `rgba(${selectedRgb.r}, ${selectedRgb.g}, ${selectedRgb.b}, 0.2)`);

  // Unselected/timeline beat colors
  root.style.setProperty('--nb-timeline-beat', colors.unselected);
  root.style.setProperty('--nb-accent-secondary', colors.unselected);
}

/**
 * Convert hex color to RGB
 */
function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : { r: 0, g: 0, b: 0 };
}
